/**
 * ViewportService.ts
 * 
 * 职责：
 * 1. 管理画布的视图变换，包括平移和缩放
 * 2. 提供坐标系转换功能，在客户端坐标和画布坐标之间转换
 * 3. 处理用户拖拽视图的交互逻辑
 * 4. 提供画布缩放功能，并保持缩放中心点不变
 * 5. 应用变换矩阵到画布上下文
 * 6. 管理画布尺寸调整
 * 7. 提供视图状态重置功能
 * 8. 维护画布的变换状态信息
 * 9. 提供平滑缩放动画功能
 */

import { Point } from '../../types';

export class ViewportService {
  private offsetX: number = 0;
  private offsetY: number = 0;
  private scale: number = 1.0;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private canvasWidth: number = 0;
  private canvasHeight: number = 0;
  
  // 平滑滚动相关属性
  private isSmoothScrolling: boolean = false;
  private smoothScrollStartTime: number = 0;
  private smoothScrollDuration: number = 150; // 增加到150ms，使动画更平滑
  private smoothScrollStartOffsetX: number = 0;
  private smoothScrollStartOffsetY: number = 0;
  private smoothScrollTargetOffsetX: number = 0;
  private smoothScrollTargetOffsetY: number = 0;
  
  // 缩放动画相关属性
  private isZooming: boolean = false;
  private targetScale: number = 1.0;
  private zoomStartScale: number = 1.0;
  private zoomCenterX: number = 0;
  private zoomCenterY: number = 0;
  private zoomStartTime: number = 0;
  private zoomDuration: number = 180; // 增加到180ms，使缩放更平滑
  private animationFrameId: number | null = null;
  private onRenderCallback: (() => void) | null = null;
  
  // 禁用缩放功能的标志
  private zoomDisabled: boolean = false;

  // 静态属性，用于追踪上次保存时间
  private static lastSaveTime: number = 0;

  // 上次触发viewport-changed事件的时间
  private lastViewportChangedTime: number = 0;
  // 节流时间间隔（毫秒）
  private readonly VIEWPORT_CHANGED_THROTTLE = 16; // 约60fps
  
  constructor() {
    // 构造函数
  }

  // 设置渲染回调函数
  public setRenderCallback(callback: (() => void) | null): void {
    this.onRenderCallback = callback;
  }

  // Reset viewport state
  public reset(): void {
    this.offsetX = 0;
    this.offsetY = 0;
    this.scale = 1.0;
    this.isDragging = false;
    
    // 取消任何正在进行的动画
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.isZooming = false;
    
    // 通知视口变化
    this.notifyViewportChanged();
  }

  // Resize canvas
  public resizeCanvas(width: number, height: number): void {
    this.canvasWidth = width;
    this.canvasHeight = height;
  }

  // Get canvas size
  public getCanvasSize(): { width: number; height: number } {
    return { width: this.canvasWidth, height: this.canvasHeight };
  }

  // Start dragging viewport
  public startDrag(x: number, y: number): void {
    this.isDragging = true;
    this.dragStartX = x;
    this.dragStartY = y;
  }

  // Drag viewport
  public drag(x: number, y: number): void {
    if (!this.isDragging) return;

    const deltaX = x - this.dragStartX;
    const deltaY = y - this.dragStartY;

    this.offsetX += deltaX / this.scale;
    this.offsetY += deltaY / this.scale;

    this.dragStartX = x;
    this.dragStartY = y;
    
    // 通知视口变化
    this.notifyViewportChanged();
  }

  // Stop dragging viewport
  public stopDrag(): void {
    // 只有在实际进行了拖动时才触发保存
    if (this.isDragging) {
      this.isDragging = false;
      
      // 触发工具操作完成事件，自动保存当前内容状态
      document.dispatchEvent(new CustomEvent('tool-operation-complete'));
    }
  }

  // 设置是否禁用缩放
  public setZoomDisabled(disabled: boolean): void {
    this.zoomDisabled = disabled;
  }

  // 获取是否禁用缩放
  public isZoomDisabled(): boolean {
    return this.zoomDisabled;
  }

  // Zoom in/out with animation
  public zoom(direction: number, centerX: number, centerY: number, isPrecise: boolean = false): void {
    // 如果缩放功能被禁用，直接返回
    if (this.zoomDisabled) {
      return;
    }
    
    // 如果已经在缩放动画中，取消之前的动画
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    // 计算当前鼠标位置在缩放前的画布坐标
    const mouseCanvasX = (centerX / this.scale) - this.offsetX;
    const mouseCanvasY = (centerY / this.scale) - this.offsetY;
    
    // 保存缩放起始值
    this.zoomStartScale = this.scale;
    this.zoomCenterX = centerX;
    this.zoomCenterY = centerY;
    
    // 根据是否精确缩放选择不同的缩放因子
    let zoomFactor;
    if (isPrecise) {
      // 精确缩放模式 - 缩放步长更小
      zoomFactor = direction > 0 ? 1.03 : 0.97; // 精确缩放时仅放大/缩小3%
    } else {
      // 正常缩放模式 - 减小缩放步长，使变化更平滑
      zoomFactor = direction > 0 ? 1.2 : (1/1.2); // 放大20%或缩小相同比例(约16.7%)
    }
    
    this.targetScale = Math.max(0.1, Math.min(10.0, this.scale * zoomFactor)); // 限制缩放范围
    
    // 使用更长的动画持续时间，使缩放更平滑
    const animationDuration = isPrecise ? 120 : this.zoomDuration;
    
    // 开始缩放动画
    this.isZooming = true;
    this.zoomStartTime = Date.now();
    
    // 启动动画循环
    this.animateZoom(mouseCanvasX, mouseCanvasY, animationDuration);
    
    // 减少保存事件触发频率，使用静态变量跟踪上次保存时间
    const now = Date.now();
    if (!ViewportService.lastSaveTime || now - ViewportService.lastSaveTime > 1000) {
      ViewportService.lastSaveTime = now;
      // 延长延迟时间，避免频繁保存
      setTimeout(() => {
        // 触发工具操作完成事件，自动保存当前内容状态
        document.dispatchEvent(new CustomEvent('tool-operation-complete'));
      }, 500);
    }
  }
  
  // 执行缩放动画
  private animateZoom(mouseCanvasX: number, mouseCanvasY: number, duration: number = this.zoomDuration): void {
    const currentTime = Date.now();
    const elapsed = currentTime - this.zoomStartTime;
    
    // 计算动画进度 (0-1)
    const progress = Math.min(elapsed / duration, 1);
    
    // 使用平滑的缓动函数，提供更自然的动画效果
    // easeOutCubic: 开始快，结束慢，感觉更自然
    const easedProgress = 1 - Math.pow(1 - progress, 3);
    
    // 计算当前缩放比例
    this.scale = this.zoomStartScale + (this.targetScale - this.zoomStartScale) * easedProgress;
    
    // 更新偏移量，保持鼠标位置下方的内容不变
    this.offsetX = -(mouseCanvasX * this.scale - this.zoomCenterX) / this.scale;
    this.offsetY = -(mouseCanvasY * this.scale - this.zoomCenterY) / this.scale;
    
    // 通知视口变化
    this.notifyViewportChanged();
    
    // 触发重新渲染
    if (this.onRenderCallback) {
      this.onRenderCallback();
    }
    
    // 如果动画未完成，继续下一帧
    if (progress < 1) {
      this.animationFrameId = requestAnimationFrame(() => this.animateZoom(mouseCanvasX, mouseCanvasY, duration));
    } else {
      // 动画完成
      this.isZooming = false;
      this.animationFrameId = null;
    }
  }

  // Check if dragging
  public isDraggingViewport(): boolean {
    return this.isDragging;
  }
  
  // Check if zooming
  public isZoomingViewport(): boolean {
    return this.isZooming;
  }

  // Convert client coordinates to canvas coordinates
  public clientToCanvas(clientX: number, clientY: number): Point {
    return {
      x: (clientX / this.scale) - this.offsetX,
      y: (clientY / this.scale) - this.offsetY
    };
  }

  // Convert canvas coordinates to client coordinates
  public canvasToClient(canvasX: number, canvasY: number): Point {
    return {
      x: (canvasX + this.offsetX) * this.scale,
      y: (canvasY + this.offsetY) * this.scale
    };
  }

  // Apply transform to context
  public applyTransform(ctx: CanvasRenderingContext2D): void {
    ctx.setTransform(this.scale, 0, 0, this.scale, this.offsetX * this.scale, this.offsetY * this.scale);
  }

  // Get current scale
  public getScale(): number {
    return this.scale;
  }

  // Get current offset
  public getOffset(): Point {
    return { x: this.offsetX, y: this.offsetY };
  }

  // 获取X轴偏移量
  public getOffsetX(): number {
    return this.offsetX;
  }

  // 获取Y轴偏移量
  public getOffsetY(): number {
    return this.offsetY;
  }

  // 发送视口变化事件
  private notifyViewportChanged(): void {
    // 使用节流函数限制事件触发频率
    if (this.throttleViewportChanged()) {
      // 创建自定义事件
      const event = new CustomEvent('viewport-changed', {
        detail: {
          scale: this.scale,
          offsetX: this.offsetX,
          offsetY: this.offsetY
        }
      });
      
      // 触发事件
      document.dispatchEvent(event);
    }
  }
  
  // 节流函数，限制viewport-changed事件的触发频率
  private throttleViewportChanged(): boolean {
    const now = Date.now();
    if (now - this.lastViewportChangedTime >= this.VIEWPORT_CHANGED_THROTTLE) {
      this.lastViewportChangedTime = now;
      return true;
    }
    return false;
  }

  // 开始平滑滚动
  public startSmoothScroll(targetOffsetX: number, targetOffsetY: number): void {
    // 如果距离很小，直接设置偏移量，不使用动画
    const distX = Math.abs(this.offsetX - targetOffsetX);
    const distY = Math.abs(this.offsetY - targetOffsetY);
    
    if (distX < 0.5 && distY < 0.5) {
      this.offsetX = targetOffsetX;
      this.offsetY = targetOffsetY;
      this.notifyViewportChanged();
      
      if (this.onRenderCallback) {
        this.onRenderCallback();
      }
      return;
    }
    
    // 如果已经在滚动，取消之前的动画并开始新的动画
    // 保存当前状态作为动画起点
    this.smoothScrollStartOffsetX = this.offsetX;
    this.smoothScrollStartOffsetY = this.offsetY;
    this.smoothScrollTargetOffsetX = targetOffsetX;
    this.smoothScrollTargetOffsetY = targetOffsetY;
    
    // 开始新的动画
    this.isSmoothScrolling = true;
    this.smoothScrollStartTime = Date.now();
    
    // 触发滚动更新事件
    document.dispatchEvent(new CustomEvent('viewport-scroll-update'));
    
    // 启动平滑滚动动画
    this.animateSmoothScroll();
  }

  // 执行平滑滚动动画
  private animateSmoothScroll(): void {
    const currentTime = Date.now();
    const elapsed = currentTime - this.smoothScrollStartTime;
    
    // 计算动画进度 (0-1)
    const progress = Math.min(elapsed / this.smoothScrollDuration, 1);
    
    // 使用更平滑的缓动函数
    // easeOutCubic: 开始快，结束慢，感觉更自然
    const easedProgress = 1 - Math.pow(1 - progress, 3);
    
    // 计算当前偏移量
    this.offsetX = this.smoothScrollStartOffsetX + (this.smoothScrollTargetOffsetX - this.smoothScrollStartOffsetX) * easedProgress;
    this.offsetY = this.smoothScrollStartOffsetY + (this.smoothScrollTargetOffsetY - this.smoothScrollStartOffsetY) * easedProgress;
    
    // 发送滚动更新事件
    document.dispatchEvent(new CustomEvent('viewport-scroll-update'));
    
    // 通知视口变化
    this.notifyViewportChanged();
    
    // 触发重新渲染
    if (this.onRenderCallback) {
      this.onRenderCallback();
    }
    
    // 如果动画未完成，继续下一帧
    if (progress < 1) {
      requestAnimationFrame(() => this.animateSmoothScroll());
    } else {
      // 动画完成
      this.isSmoothScrolling = false;
      
      // 在动画完成后再次渲染，确保最终状态正确显示
      if (this.onRenderCallback) {
        this.onRenderCallback();
      }
    }
  }

  // 检查是否正在平滑滚动
  public isSmoothScrollingViewport(): boolean {
    return this.isSmoothScrolling;
  }

  // 设置当前缩放和偏移量（用于从保存的状态恢复）
  public setViewportState(scale: number, offsetX: number, offsetY: number): void {
    this.scale = scale;
    this.offsetX = offsetX;
    this.offsetY = offsetY;
    
    // 通知视口变化
    this.notifyViewportChanged();
    
    // 触发重新渲染
    if (this.onRenderCallback) {
      this.onRenderCallback();
    }
  }
  
  // 获取当前视口状态（用于保存）
  public getViewportState(): { scale: number; offsetX: number; offsetY: number } {
    return {
      scale: this.scale,
      offsetX: this.offsetX,
      offsetY: this.offsetY
    };
  }

  // 屏幕坐标转画布坐标
  public screenToCanvas(point: Point): Point {
    return {
      x: (point.x / this.scale) - this.offsetX,
      y: (point.y / this.scale) - this.offsetY
    };
  }

  // 导航到内容中心，接收内容边界参数
  public navigateToContentCenter(contentBounds: { minX: number; minY: number; maxX: number; maxY: number }, animate: boolean = true): void {
    // 检查边界是否有效
    if (contentBounds.minX === Number.MAX_VALUE || 
        contentBounds.maxX === -Number.MAX_VALUE || 
        contentBounds.minY === Number.MAX_VALUE || 
        contentBounds.maxY === -Number.MAX_VALUE) {
      // 边界无效，可能没有内容，直接返回到原点
      if (animate) {
        this.startSmoothScroll(0, 0);
      } else {
        this.offsetX = 0;
        this.offsetY = 0;
        this.notifyViewportChanged();
      }
      return;
    }
    
    // 计算内容的中心点
    const contentCenterX = (contentBounds.minX + contentBounds.maxX) / 2;
    const contentCenterY = (contentBounds.minY + contentBounds.maxY) / 2;
    
    // 计算画布中心点（相对于左上角的偏移）
    const canvasCenterX = this.canvasWidth / (2 * this.scale);
    const canvasCenterY = this.canvasHeight / (2 * this.scale);
    
    // 计算需要的偏移量，使内容中心与画布中心对齐
    const targetOffsetX = canvasCenterX - contentCenterX;
    const targetOffsetY = canvasCenterY - contentCenterY;
    
    // 检查当前偏移量与目标偏移量的差距，如果很小则不移动
    const currentDistX = Math.abs(this.offsetX - targetOffsetX);
    const currentDistY = Math.abs(this.offsetY - targetOffsetY);
    
    if (currentDistX < 1 && currentDistY < 1) {
      // 差距很小，不需要移动
      return;
    }
    
    // 根据参数决定是否使用动画
    if (animate) {
      this.startSmoothScroll(targetOffsetX, targetOffsetY);
    } else {
      this.offsetX = targetOffsetX;
      this.offsetY = targetOffsetY;
      this.notifyViewportChanged();
    }
    
    // 触发重新渲染
    if (this.onRenderCallback) {
      this.onRenderCallback();
    }
    
    // 减少保存事件触发频率
    const now = Date.now();
    if (!ViewportService.lastSaveTime || now - ViewportService.lastSaveTime > 1000) {
      ViewportService.lastSaveTime = now;
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('tool-operation-complete'));
      }, 200);
    }
  }
}